import type { Observable} from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import type { HierarchyNode } from '@bayer-int/portletce-shared';

import type { Boot } from 'portlet/config/boot';
import type { HttpService } from 'portlet/common/services';

import type { HierarchyVariant, HierarchyVariantsService } from './HierarchyVariantsService';

export interface Dimension {
    /** human friendly identifier */
    internalKey: string;
    /** identifier related to SAP BW  */
    characteristicId: string;
    /** references another dimension that the dimension is modification of. By default and in most cases it is equal to `internalKey` */
    basedOnInternalKey: string;
    /** references another dimension that the dimension is modification of. By default and in most cases it is equal to `characteristicId` */
    basedOnCharacteristicId: string;
    characteristicNameShort: string;
    characteristicNameLong: string;
    defaultHierarchy: string;
    key: string;
    label: string;
}

export interface DimensionValue {
    key: string;
    label: string;
    value: string;
    identifier: string;
    internalKey: string;
    characteristicId: string;
    basedOnInternalKey: string;
    basedOnCharacteristicId: string;
}

interface CommonHierarchy {
    hierarchyId: string;
    hierarchyName: string;
    characteristicNameShort: string;
    characteristicNameLong: string;
    /** identifier related to SAP BW */
    characteristicIds: Array<string>;
    /** human friendly identifiers which the hierarchy consists of */
    internalKeys: Array<string>;
    /** see Dimension#characteristicId */
    basedOnCharacteristicIds: Array<string>;
    /** see Dimension#basedOnInternalKey */
    basedOnInternalKeys: Array<string>;
    key: string;
    label: string;
}

export type Hierarchy = CommonHierarchy &
    ({ type: 'real' } | { type: 'virtual' } | { type: 'variant'; dataSource: string });

export class MasterDataService {
    private masterDataUrl = this.boot.servers.masterData.url;

    constructor(
        private readonly http: HttpService,
        private readonly variants: HierarchyVariantsService,
        private readonly boot: Boot,
    ) {}

    public getDimensions(): Observable<Dimension[]> {
        return this.http
            .get<Dimension[]>(`${this.masterDataUrl}dimension/`, { credentials: 'include' })
            .pipe(map(({ data }) => data));
    }

    public getDimensionValues(internalKey: string): Observable<DimensionValue[]> {
        return this.http
            .get<DimensionValue[]>(`${this.masterDataUrl}dimension/${internalKey}`, {
                credentials: 'include',
            })
            .pipe(map(({ data }) => data));
    }

    public getHierarchies(types: Array<Hierarchy['type']> = []): Observable<Hierarchy[]> {
        return this.http
            .get<Hierarchy[]>(
                `${this.masterDataUrl}hierarchy/?${types.map(type => `type=${type}`).join('&')}`,
                { credentials: 'include' },
            )
            .pipe(map(({ data }) => data));
    }

    public getHierarchyMetadata(hierarchyId: string): Observable<Hierarchy> {
        return this.http
            .get<Hierarchy>(`${this.masterDataUrl}hierarchy/${hierarchyId}`, {
                credentials: 'include',
                query: { field: 'metadata' },
            })
            .pipe(map(({ data }) => data));
    }

    /** returns list of hierarchies that have at least one dimension in common, applicable only to virtual hierarchies */
    public getIntersectingHierarchies(hierarchyId: string): Observable<Hierarchy[]> {
        return this.http
            .get<Hierarchy[]>(
                `${this.masterDataUrl}virtual-hierarchy/?intersection=${hierarchyId}`,
                { credentials: 'include' },
            )
            .pipe(map(({ data }) => data));
    }

    public getHierarchiesBasedOnInternalKey(internalKey: string): Observable<Hierarchy[]> {
        return this.http
            .get<Hierarchy[]>(
                `${this.masterDataUrl}virtual-hierarchy/?internalKey=${internalKey}`,
                {
                    credentials: 'include',
                },
            )
            .pipe(map(({ data }) => data));
    }

    public getHierarchyVariants(dataSource: string): Observable<HierarchyVariant[]> {
        return this.variants.getHierarchyVariants(dataSource);
    }

    public getDimensionNodes(internalKey: string, keys: string[]): Observable<DimensionValue[]> {
        if (keys.length === 0) return of([]);

        return this.http
            .get<DimensionValue[]>(`${this.masterDataUrl}dimension/${internalKey}`, {
                credentials: 'include',
                query: { keys },
            })
            .pipe(map(({ data }) => data));
    }

    public getHierarchyNodes(
        hierarchyId: string,
        internalKey: string | null | undefined,
        keys: string[],
    ): Observable<Omit<HierarchyNode, 'children'>[]> {
        if (keys.length === 0) return of([]);

        const query = { keys, ...(internalKey && { internalKey }) };

        return this.http
            .get<Omit<HierarchyNode, 'children'>[]>(
                `${this.masterDataUrl}hierarchy/${hierarchyId}`,
                {
                    credentials: 'include',
                    query,
                },
            )
            .pipe(map(({ data }) => data));
    }

    public getHierarchyById(hierarchyId: string): Observable<HierarchyNode[]> {
        return this.http
            .get<HierarchyNode[]>(`${this.masterDataUrl}hierarchy/${hierarchyId}`, {
                credentials: 'include',
            })
            .pipe(map(({ data }) => data));
    }
}
